import { type ReactElement, useCallback, useMemo, useState } from 'react';

import type * as api from '@gitbook/api-client';
import {
    Card,
    Description,
    type Key,
    type Selection,
    useDismissedState,
} from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';

import { flattenSiteStructure, type SiteCapability, type UseUpgradeSite } from '../../../state';
import { EnterpriseMigrationCTA } from '../../Billing';
import { AddSiteSectionToGroupButton } from './AddSiteSectionToGroupButton';
import { CreateRootSiteItemButton } from './CreateRootSiteItemButton';
import { CreateSiteSectionButton } from './CreateSiteSectionButton';
import { CreateSiteSectionGroupButton } from './CreateSiteSectionGroupButton';
import { CreateSiteVariantButton } from './CreateSiteVariantButton';
import { DeleteSiteItemsButton } from './DeleteSiteItemsButton';
import { EditSiteSectionForm } from './EditSiteSectionForm';
import { EditSiteSectionGroupForm } from './EditSiteSectionGroupForm';
import { EditSiteVariantForm } from './EditSiteVariantForm';
import { MoveSiteSectionsButton } from './MoveSiteSectionsButton';
import { moveSiteItem } from './moveSiteItem';
import { SetSectionAsDefaultButton } from './SetSectionAsDefaultButton';
import { SetVariantAsDefaultButton } from './SetVariantAsDefaultButton';
import { SiteStructureTree, type SiteStructureTreeProps } from './SiteStructureTree';

export function SiteStructureEditor(props: {
    sitePermissions: SiteCapability;
    site: api.Site;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    siteStructure: api.SiteStructure;
    upgrade: UseUpgradeSite | null;
}) {
    const { sitePermissions, organization, orgAllContent, siteStructure, site, upgrade } = props;
    const [isReordering, wrapReordering] = useLoadingState();
    const [enterpriseCTAIsDismissed, dismissEnterpriseCTA] = useDismissedState(
        `enterprise-migration-cta-${organization.id}`
    );

    const handleCreateVariant = (siteSpace: api.SiteSpace, siteSection: api.SiteSection | null) => {
        setSelectedKeys(new Set([siteSpace.id]));
        if (siteSection) {
            setExpandedKeys((keys) => {
                if (keys.has(siteSection.id)) {
                    return keys;
                }
                const newKeys = new Set(keys);
                newKeys.add(siteSection.id);
                return newKeys;
            });
        }
    };

    const handleReorder: SiteStructureTreeProps['onReorder'] = useEventCallback((props) => {
        wrapReordering(moveSiteItem({ ...props, organization, site, siteStructure }));
    });

    const flattenedItems = useMemo(() => flattenSiteStructure(siteStructure), [siteStructure]);

    // select the first item by default
    const [selectedKeys, setSelectedKeys] = useState<Set<Key>>(
        new Set(flattenedItems[0] ? [flattenedItems[0].id] : [])
    );
    const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(new Set());

    const handleSelectionChange = useCallback((selection: Selection) => {
        if (selection === 'all') {
            // We don't support selecting all items
            return;
        }
        setSelectedKeys(selection);
    }, []);

    const closeSelection = useEventCallback(() => {
        setSelectedKeys(new Set());
    });

    const selectedItems = useMemo(() => {
        return Array.from(selectedKeys)
            .map((key) => flattenedItems.find((item) => item.id === key))
            .filter((x) => x !== undefined);
    }, [flattenedItems, selectedKeys]);

    const singleSiteSectionGroup =
        selectedItems.length === 1 && selectedItems[0].object === 'site-section-group'
            ? selectedItems[0]
            : null;

    const singleSiteSection =
        selectedItems.length === 1 && selectedItems[0].object === 'site-section'
            ? selectedItems[0]
            : null;

    const singleSiteSpace =
        selectedItems.length === 1 && selectedItems[0].object === 'site-space'
            ? selectedItems[0]
            : null;

    const actions = (() => {
        // If the user cannot admin the site, we don't show any actions
        if (!sitePermissions.canAdminSite) {
            return [];
        }

        if (selectedItems.length > 0) {
            const actions: ReactElement[] = [];

            if (singleSiteSpace) {
                if (!singleSiteSpace.default) {
                    actions.push(
                        <SetVariantAsDefaultButton
                            key="set-default"
                            organization={organization}
                            site={site}
                            siteSpace={singleSiteSpace}
                            siteStructure={siteStructure}
                            label="Set as home"
                            size="small"
                        />
                    );
                }
            }

            if (selectedItems.every((item) => item.object === 'site-section')) {
                if (singleSiteSection) {
                    actions.push(
                        <CreateSiteVariantButton
                            key="create-variant"
                            orgAllContent={orgAllContent}
                            organization={organization}
                            siteStructure={siteStructure}
                            site={site}
                            siteSection={singleSiteSection}
                            label="Add variant"
                            size="small"
                            onCreateVariant={handleCreateVariant}
                        />
                    );

                    if (!singleSiteSection.default) {
                        actions.push(
                            <SetSectionAsDefaultButton
                                key="set-default"
                                organization={organization}
                                site={site}
                                siteSection={singleSiteSection}
                                label="Set as home"
                                size="small"
                            />
                        );
                    }
                }

                actions.push(
                    <MoveSiteSectionsButton
                        key="group-sections"
                        label="Move to…"
                        siteSections={selectedItems}
                        organization={organization}
                        site={site}
                        siteStructure={siteStructure}
                        size="small"
                        upgrade={upgrade}
                    />
                );
            }

            if (singleSiteSectionGroup) {
                actions.push(
                    <AddSiteSectionToGroupButton
                        key="create-section"
                        organization={organization}
                        siteStructure={siteStructure}
                        site={site}
                        label="Add section…"
                        siteSectionGroup={singleSiteSectionGroup}
                        size="small"
                        upgrade={upgrade}
                    />
                );
            }

            actions.push(
                <DeleteSiteItemsButton
                    key="delete-items"
                    items={selectedItems}
                    organization={organization}
                    site={site}
                    label="Remove"
                    size="small"
                />
            );

            return actions;
        }
        return siteStructure.type === 'siteSpaces' || upgrade
            ? [
                  <CreateRootSiteItemButton
                      key="create-any"
                      orgAllContent={orgAllContent}
                      organization={organization}
                      siteStructure={siteStructure}
                      site={site}
                      label="Add…"
                      size="small"
                      upgrade={upgrade}
                  />,
              ]
            : [
                  <CreateSiteSectionButton
                      key="create-section"
                      organization={organization}
                      site={site}
                      siteStructure={siteStructure}
                      label="Add section"
                      size="small"
                  />,
                  <CreateSiteSectionGroupButton
                      key="create-group"
                      label="Add group"
                      organization={organization}
                      site={site}
                      siteSections={[]}
                      size="small"
                  />,
              ];
    })();

    return (
        <Card className="flex flex-col overflow-hidden md:h-[40rem] md:max-h-[calc(100vh-var(--app-unavailable-height)-2rem)] md:flex-row">
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: fine to not have keyboard for that */}
            <div
                className="scrollbar relative flex flex-col gap-2 overflow-auto p-2 md:max-w-96 md:flex-1"
                onClick={() => {
                    closeSelection();
                }}
            >
                <div className="flex items-center justify-between gap-4 pl-2">
                    <h2 className="font-medium text-ui-heading-small">Structure tree</h2>
                    <div className="flex shrink-0 items-center gap-2">
                        {sitePermissions.canAdminSite ? (
                            <CreateRootSiteItemButton
                                label="Add…"
                                organization={organization}
                                site={site}
                                siteStructure={siteStructure}
                                orgAllContent={orgAllContent}
                                upgrade={upgrade}
                                iconOnly
                                size="small"
                                kind="blank"
                            />
                        ) : null}
                    </div>
                </div>
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: fine to not have keyboard for that */}
                <div
                    className="contents"
                    // Avoid unselecting when clicking between the lines or anywhere in the tree
                    onClick={(event) => event.stopPropagation()}
                >
                    <SiteStructureTree
                        site={site}
                        siteStructure={siteStructure}
                        organization={organization}
                        orgAllContent={orgAllContent}
                        selectedKeys={selectedKeys}
                        onSelectionChange={handleSelectionChange}
                        sitePermissions={sitePermissions}
                        onReorder={handleReorder}
                        isDisabled={isReordering}
                        upgrade={upgrade}
                        className="flex-1"
                        expandedKeys={expandedKeys}
                        setExpandedKeys={setExpandedKeys}
                        onCreateVariant={handleCreateVariant}
                    />
                    {enterpriseCTAIsDismissed ? null : (
                        <EnterpriseMigrationCTA
                            organization={organization}
                            onClose={dismissEnterpriseCTA}
                        />
                    )}
                </div>
            </div>
            <div className="flex flex-col max-md:min-h-48 max-md:border-t md:flex-1 md:border-l">
                <div className="flex min-h-0 flex-1 flex-col empty:hidden">
                    {selectedItems.length === 0 ? (
                        <div className="flex flex-1 flex-col items-center justify-center px-4 py-2">
                            <div className="mb-1 text-ui-heading-small">Nothing selected</div>
                            <Description className="mb-2">
                                Select a section, group, or variant
                                {sitePermissions.canAdminSite
                                    ? " - or add to your site's structure"
                                    : ''}
                                .
                            </Description>
                            <div className="flex shrink-0 items-center gap-2">{actions}</div>
                        </div>
                    ) : selectedItems.length > 1 ? (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-2">
                            <div className="text-ui-heading-small">
                                {selectedItems.length} {(() => {
                                    if (
                                        selectedItems.every(
                                            (item) => item.object === 'site-section'
                                        )
                                    ) {
                                        return 'sections';
                                    }
                                    if (
                                        selectedItems.every(
                                            (item) => item.object === 'site-section-group'
                                        )
                                    ) {
                                        return 'groups';
                                    }
                                    if (
                                        selectedItems.every((item) => item.object === 'site-space')
                                    ) {
                                        return 'variants';
                                    }
                                    return 'items';
                                })()} selected
                            </div>
                            <div className="flex shrink-0 items-center gap-2">{actions}</div>
                        </div>
                    ) : (
                        <div className="flex min-h-0 flex-col overflow-y-auto">
                            <div className="sticky top-0 isolate z-10 flex flex-wrap items-center justify-between gap-2 border-b bg-base/80 p-2 pl-4 backdrop-blur">
                                <h3 className="text-ui-heading-small">
                                    {singleSiteSectionGroup ? 'Group' : null}
                                    {singleSiteSection ? 'Section' : null}
                                    {singleSiteSpace ? 'Variant' : null}
                                </h3>
                                <div className="group flex min-w-0 flex-wrap items-center gap-2 empty:hidden">
                                    {actions}
                                </div>
                            </div>
                            <div className="flex-1">
                                {singleSiteSectionGroup ? (
                                    <EditSiteSectionGroupForm
                                        key={singleSiteSectionGroup.id}
                                        siteSectionGroup={singleSiteSectionGroup}
                                        organization={organization}
                                        site={site}
                                        disabled={!sitePermissions.canAdminSite}
                                    />
                                ) : null}
                                {singleSiteSection ? (
                                    <EditSiteSectionForm
                                        key={singleSiteSection.id}
                                        siteSection={singleSiteSection}
                                        organization={organization}
                                        site={site}
                                        orgAllContent={orgAllContent}
                                        siteStructure={siteStructure}
                                        disabled={!sitePermissions.canAdminSite}
                                    />
                                ) : null}
                                {singleSiteSpace ? (
                                    <EditSiteVariantForm
                                        key={singleSiteSpace.id}
                                        siteSpace={singleSiteSpace}
                                        organization={organization}
                                        orgAllContent={orgAllContent}
                                        siteStructure={siteStructure}
                                        site={site}
                                        disabled={!sitePermissions.canAdminSite}
                                    />
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}
